import styled from "styled-components";
import { useState } from "react";
import { addComment, increaseCommentLike } from "../api/contents";
import { isLoggedIn } from "../login/Login";
import { DateTag } from "../home/Contents";
import { getDate, getSortedComments } from "../public/getDate";
import { LikeIcon, LikeContainer, CalenderIconContainer, CalendarIcon, AdminShieldIcon, VisitorIcon } from "../icons";
import { BtnHoverAnimation } from "../animes";
import { CommentIF } from "../types";

/* Comments.tsx는 Content 내의 댓글 창을 담당 */

interface CommentsProps {
    comments: CommentIF[],
    onComments: React.Dispatch<React.SetStateAction<CommentIF[]>>,
    content_id: number
}

const CommentBox = styled.div`
    margin-top: 1rem;
    width: 60%;
    min-height: 4rem;

    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 7px;
    background: linear-gradient(325deg, #fcfcac 0%, #ffff69);
`;

const AdminCommentBox = styled.div`
    margin-top: 1rem;
    margin-left: auto;
    width: 60%;
    min-height: 4rem;

    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 7px;
    background: linear-gradient(45deg, #a9ffd0 0%, #41fa95);
    align-content: flex-end;
`;

const CommentInputRow = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 0.1rem;
`;

const CommentInput = styled.textarea.attrs({placeholder: "내용을 입력하세요..."})`
    width: 92%;
    height: 4rem;
    margin: 1rem auto 0 0;
    padding: 10px 0 0  10px;

    font-size: 0.95rem;
    font-family: inherit;
    border-radius: 7px;
    border: 1px solid ${props => props.theme.SEARCH.BORDER};
    background: ${props => props.theme.SEARCH.BACKGROUND};
    box-shadow: inset 0 2px 4px ${props => props.theme.SEARCH.SHADOW};
    color: ${props => props.theme.CONTENT.TITLE_COLOR};

    &:focus {
        outline: none;
        border-color: ${props => props.theme.SEARCH.FOCUS.BORDER};
        box-shadow: inset 0 2px 4px ${props => props.theme.SEARCH.FOCUS.SHADOW};
    }

    &::placeholder {
        color: ${props => props.theme.CONTENT.TITLE_COLOR};
    }
`;

const EnterBtn = styled.button`
    width: 5%;
    height: 4.8rem;
    margin-left:auto;   

    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 1rem;
    transition: all 0.5s ease;
    color: white;

    letter-spacing: -1px;
    font-weight: 600;

    &:hover {
        animation: ${props => BtnHoverAnimation(props.theme.BTN_ENTER.HOVER.BACKGROUND.FIRST, props.theme.BTN_ENTER.HOVER.BACKGROUND.LAST)} 0.3s;
        background-color: ${props => props.theme.BTN_ENTER.HOVER.BACKGROUND.LAST};
        box-shadow: inset 0 6px 6px ${props => props.theme.BTN_ENTER.HOVER.SHADOW};
    }

    &:not(:hover) {
        animation: ${props => BtnHoverAnimation(props.theme.BTN_ENTER.HOVER.BACKGROUND.LAST, props.theme.BTN_ENTER.HOVER.BACKGROUND.FIRST)} 0.3s;
        background-color: ${props => props.theme.BTN_ENTER.HOVER.BACKGROUND.FIRST};
    }
`;

const Upper = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin-top: 0.2rem;
`;

const User = styled.div`
    padding: 0.5rem 0 0 1.2rem;
`;

const Comment = styled.div`
    width: 100%;
    padding: 1rem;
    
    box-sizing:border-box;
    word-break: break-all;

    margin: 0;
    
    text-align: left;
    font-size: 1rem;
    font-weight: lighter;
`;

const Like = styled.span`
    padding-right: 0.5rem;
`;

const Title = styled.div`
    letter-spacing: -0.05rem;
    color: ${props => props.theme.CONTENT.TITLE_COLOR};
`;

function Comments({comments, onComments, content_id}:CommentsProps) {
    const [newComment, setNewComment] = useState<string>("");
    // 댓글 등록 시 이벤트
    const submitComment = async () => {
        if (!newComment.trim()) return alert("내용을 입력해주세요.");
        const {year, month, day, hour, min, sec} = getDate();
        const newDate = `${year}-${month}-${day} ${hour}:${min}:${sec}`;
        const newIsAdmin = (isLoggedIn()) ? true : false;
        const newId = Date.now();

        const addedComment = {
            isAdmin: newIsAdmin,
            comment: newComment,
            date: newDate,
            LikeCnt: 0,
            id: newId
        }

        onComments(getSortedComments([...comments, addedComment]));
        setNewComment(""); // 입력창 비우기

        try {
            await addComment(
                content_id,
                newIsAdmin,
                newComment,
                newDate,
                0, 
                newId); // Date component랑 이름 겹쳐서 이렇게 수정
        } catch(err) {
            alert("댓글 등록에 실패했습니다.")
            console.log(err);
            // 실패했다면 롤백
            onComments(prev => prev.filter(cmt => cmt.id !== newId));
        }
    }

    // 좋아요 갱신
    const LikePlusOne = async (comment_id: number) => {
        // 1. UI 상태 갱신
        onComments(prev => prev.map(cmt => 
            cmt.id === comment_id ? { ...cmt, LikeCnt: cmt.LikeCnt + 1 } : cmt
        ));

        // 2. 서버에 요청
        try {
            await increaseCommentLike(content_id, comment_id);
        } catch(err) {
            console.log(err);
        }
    }

    return <>
    <Title>Comments</Title>
            {comments.map(cmt => (
                cmt.isAdmin
                ? <AdminCommentBox key={cmt.id}>
                    <Upper>
                        <User><AdminShieldIcon /></User>
                        <DateTag>
                            <CalenderIconContainer>
                                <CalendarIcon />
                            </CalenderIconContainer>
                            {cmt.date.split(" ")[0]}
                        </DateTag>
                        <LikeContainer onClick={() => LikePlusOne(cmt.id)}>
                            <LikeIcon />
                            <Like>{cmt.LikeCnt}</Like>
                        </LikeContainer>
                    </Upper>
                    <Comment>{cmt.comment}</Comment>
                </AdminCommentBox>
                : <CommentBox key={cmt.id}>
                    <Upper>
                        <User><VisitorIcon /></User>
                        <DateTag>
                            <CalenderIconContainer>
                                <CalendarIcon />
                            </CalenderIconContainer>
                            {cmt.date.split(" ")[0]}
                        </DateTag>
                        <LikeContainer onClick={(e) => {e.stopPropagation(); LikePlusOne(cmt.id);}}>
                            <LikeIcon />
                            <Like>{cmt.LikeCnt}</Like>
                        </LikeContainer>
                    </Upper>
                    <Comment>{cmt.comment}</Comment>
                </CommentBox>
            ))}
            <CommentInputRow>
                <CommentInput value={newComment} onChange={(e) => {setNewComment(e.target.value)}}/>
                <EnterBtn type="button" onClick={(e) => {e.stopPropagation(); submitComment();}}>Enter</EnterBtn>
            </CommentInputRow>
    </>
}

export default Comments;
